// src/components/form/FormFields.js
// Поля формы со статичной подписью над полем (вход, регистрация, подключение сервера)
import React, { useState } from 'react';
import { Box, IconButton, InputBase, Typography } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { CARD_BG_BOTTOM, CARD_LABEL_COLOR, MUTED_TEXT_COLOR } from '../../styles/tokens';

const ACCENT = '#ff7043';

// Поле без floating label — подпись не наезжает на автозаполненное значение; multiline — растёт по тексту
export function FormField({ id, label, hint, endAdornment, inputMode, maxLength, ...inputProps }) {
  const multiline = Boolean(inputProps.multiline);
  const nativeInputProps = {
    ...(inputMode && { inputMode }),
    ...(maxLength && { maxLength }),
    ...(hint && { 'aria-describedby': `${id}-hint` }),
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box component="label" htmlFor={id} sx={{ fontSize: '14px', color: CARD_LABEL_COLOR }}>
        {label}
      </Box>
      <InputBase
        id={id}
        fullWidth
        endAdornment={endAdornment}
        // inputMode / maxLength — атрибуты самого <input>, у InputBase таких пропов нет
        inputProps={Object.keys(nativeInputProps).length ? nativeInputProps : undefined}
        {...inputProps}
        sx={{
          height: multiline ? 'auto' : 48,
          minHeight: 48,
          px: 1.5,
          py: multiline ? 1.25 : 0,
          alignItems: multiline ? 'flex-start' : 'center',
          pr: endAdornment ? 0.5 : 1.5,
          backgroundColor: CARD_BG_BOTTOM,
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '10px',
          color: '#fff',
          fontSize: '16px', // 16px — iOS не зумит поле при фокусе
          '&.Mui-focused': {
            borderColor: ACCENT,
            boxShadow: '0 0 0 3px rgba(255,112,67,0.2)',
          },
          '&.Mui-disabled': { opacity: 0.6 },
          '& input': { p: 0, height: '100%' },
          '& textarea': { p: 0, lineHeight: 1.45 },
          '& input.Mui-disabled': { WebkitTextFillColor: 'rgba(255,255,255,0.7)' },
          // Перекрываем фон автозаполнения браузера
          '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 1000px ${CARD_BG_BOTTOM} inset`,
            WebkitTextFillColor: '#ffffff',
            caretColor: '#ffffff',
            transition: 'background-color 9999s',
          },
        }}
      />
      {hint && (
        <Typography id={`${id}-hint`} sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

// Поле пароля с кнопкой «показать/скрыть»
export function PasswordField(props) {
  const [visible, setVisible] = useState(false);

  return (
    <FormField
      {...props}
      type={visible ? 'text' : 'password'}
      endAdornment={
        <IconButton
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Скрыть пароль' : 'Показать пароль'}
          aria-pressed={visible}
          disabled={props.disabled}
          sx={{ width: 44, height: 44, color: 'rgba(255,255,255,0.6)' }}
        >
          {visible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      }
    />
  );
}
